// Sender-side noise gate for the microphone.
//
// A window is silent most of the time, and a microphone's own noise floor is
// audible on the far side as a hiss (iOS never lets call audio go fully quiet).
// This passes the microphone through a gain that sits at zero until the level
// rises clearly above the floor, opens within a few milliseconds, and closes
// again a moment after the room goes quiet. The floor is learned continuously, so
// a noisy fridge or a quiet studio both work without a setting.
//
// Returns a stream with the same video track and the gated audio track, plus a
// stop() to release the audio graph. If the audio context cannot run (some
// browsers keep it suspended until a tap), the original stream is returned as is.

const POLL_MS = 40;
const OPEN_ABOVE_FLOOR = 3.5;   // open when the level is this many times the floor
const MIN_OPEN_LEVEL = 0.006;   // never open below this RMS, whatever the floor
const HOLD_MS = 700;            // stay open this long after the last loud sample
const ATTACK_S = 0.015;
const RELEASE_S = 0.25;
const FLOOR_RISE = 0.002;       // how fast the learned floor may creep up per poll
const FLOOR_FALL = 0.5;         // and how readily it drops to a quieter level

export async function gateMicrophone(stream) {
  const audioTrack = stream && stream.getAudioTracks()[0];
  if (!audioTrack) return { stream, stop() {}, gated: false };
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return { stream, stop() {}, gated: false };
  let ctx;
  try {
    ctx = new Ctx();
    if (ctx.state !== "running") await ctx.resume().catch(() => {});
  } catch (e) {
    return { stream, stop() {}, gated: false };
  }
  if (ctx.state !== "running") {
    try { await ctx.close(); } catch (e) { /* ignore */ }
    console.log("noise gate: audio context not running; microphone passed through");
    return { stream, stop() {}, gated: false };
  }

  const source = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  const gain = ctx.createGain();
  gain.gain.value = 0;
  const sink = ctx.createMediaStreamDestination();
  source.connect(analyser);
  source.connect(gain);
  gain.connect(sink);

  const buffer = new Float32Array(analyser.fftSize);
  let floor = 0.02;
  let open = false;
  let lastLoud = 0;
  let opens = 0;
  const timer = setInterval(() => {
    analyser.getFloatTimeDomainData(buffer);
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) sum += buffer[i] * buffer[i];
    const rms = Math.sqrt(sum / buffer.length);
    // Learn the floor from the quiet moments: drop quickly, rise slowly.
    if (rms < floor) floor = floor - (floor - rms) * FLOOR_FALL;
    else floor = Math.min(floor + FLOOR_RISE, rms);
    const threshold = Math.max(floor * OPEN_ABOVE_FLOOR, MIN_OPEN_LEVEL);
    const now = performance.now();
    if (rms > threshold) lastLoud = now;
    const shouldOpen = now - lastLoud < HOLD_MS;
    if (shouldOpen !== open) {
      open = shouldOpen;
      const t = ctx.currentTime;
      gain.gain.cancelScheduledValues(t);
      gain.gain.setValueAtTime(gain.gain.value, t);
      gain.gain.linearRampToValueAtTime(open ? 1 : 0, t + (open ? ATTACK_S : RELEASE_S));
      if (open) opens++;
    }
  }, POLL_MS);

  const gatedTrack = sink.stream.getAudioTracks()[0];
  const out = new MediaStream([...stream.getVideoTracks(), gatedTrack]);
  console.log("noise gate: on");
  return {
    stream: out,
    gated: true,
    stop() {
      clearInterval(timer);
      try { gatedTrack.stop(); } catch (e) { /* ignore */ }
      try { source.disconnect(); gain.disconnect(); analyser.disconnect(); } catch (e) { /* ignore */ }
      ctx.close().catch(() => {});
      console.log("noise gate: off after " + opens + " opening(s)");
    },
  };
}
